/**
 * iGaming payment hold — data-layer client for Feature #157.
 *
 * Compliance-review payment hold specific to iGaming: each held conversion
 * carries a compliance flag (aml_check, kyc_pending, duplicate_ftd, etc.)
 * and moves through a small state machine (pending_review → approved |
 * rejected | escalated). All endpoints are tenant-scoped by the API layer
 * via `x-tenant-id`.
 *
 * Thin typed wrapper over `apiClient` for:
 *   - POST   /api/v1/igaming-payment-hold
 *   - GET    /api/v1/igaming-payment-hold
 *   - GET    /api/v1/igaming-payment-hold/:id
 *   - GET    /api/v1/igaming-payment-hold/:id/events
 *   - POST   /api/v1/igaming-payment-hold/:id/approve
 *   - POST   /api/v1/igaming-payment-hold/:id/reject
 *   - POST   /api/v1/igaming-payment-hold/:id/escalate
 *   - GET    /api/v1/igaming-payment-hold/stats/summary
 *
 * Pure helpers (formatters, validators, queryKeys) live in this file so
 * they can be unit-tested without pulling in the React tree.
 */
import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Vocabulary — mirrors backend constants in
// apps/api/src/routes/igaming-payment-hold.ts
// ---------------------------------------------------------------------------

export const HOLD_FLAGS = [
	"aml_check",
	"kyc_pending",
	"geo_restricted",
	"duplicate_ftd",
	"suspicious_deposit",
	"bonus_abuse",
	"rg_self_exclusion",
	"manual_review",
] as const;
export type HoldFlag = (typeof HOLD_FLAGS)[number];

export const HOLD_STATUSES = [
	"pending_review",
	"approved",
	"rejected",
	"escalated",
] as const;
export type HoldStatus = (typeof HOLD_STATUSES)[number];

export const EVENT_TYPES = [
	"created",
	"approved",
	"rejected",
	"escalated",
	"note",
] as const;
export type HoldEventType = (typeof EVENT_TYPES)[number];

export const MAX_LIST_LIMIT = 500;
export const DEFAULT_LIST_LIMIT = 100;
export const MAX_REASON_LEN = 1_000;
export const MAX_NOTE_LEN = 1_000;
export const MAX_ACTOR_LEN = 128;

// ---------------------------------------------------------------------------
// Labels — operator-facing. No emojis, no competitor names.
// ---------------------------------------------------------------------------

export const FLAG_LABELS: Record<HoldFlag, string> = {
	aml_check: "AML check",
	kyc_pending: "KYC pending",
	geo_restricted: "Geo-restricted",
	duplicate_ftd: "Duplicate FTD",
	suspicious_deposit: "Suspicious deposit",
	bonus_abuse: "Bonus abuse",
	rg_self_exclusion: "Responsible gambling",
	manual_review: "Manual review",
};

export const STATUS_LABELS: Record<HoldStatus, string> = {
	pending_review: "Pending review",
	approved: "Approved",
	rejected: "Rejected",
	escalated: "Escalated",
};

// ---------------------------------------------------------------------------
// Response types — mirror HoldRow / HoldEventRow on the API side.
// ---------------------------------------------------------------------------

export interface PaymentHold {
	id: string;
	tenant_id: string;
	conversion_id: string;
	affiliate_id: string;
	brand_id: string | null;
	offer_id: string | null;
	player_ref: string | null;
	payout_cents: number;
	currency: string | null;
	flag: HoldFlag;
	status: HoldStatus;
	reason: string | null;
	flagged_by: string | null;
	reviewed_by: string | null;
	reviewed_at: number | null;
	decision_note: string | null;
	created_at: number;
	updated_at: number;
}

export interface HoldEvent {
	id: string;
	tenant_id: string;
	hold_id: string;
	event_type: HoldEventType;
	from_status: HoldStatus | null;
	to_status: HoldStatus | null;
	actor: string | null;
	note: string | null;
	created_at: number;
}

export interface ListResponse {
	tenant_id: string;
	count: number;
	limit: number;
	holds: PaymentHold[];
}

export interface EventsResponse {
	hold_id: string;
	count: number;
	events: HoldEvent[];
}

export interface StatsSummary {
	tenant_id: string;
	total: number;
	by_status: Record<HoldStatus, number>;
	by_flag: Record<HoldFlag, number>;
}

export interface DecisionResponse {
	hold: PaymentHold;
	payout_blocked: boolean;
}

// ---------------------------------------------------------------------------
// Validators (pure — unit-testable)
// ---------------------------------------------------------------------------

const ID_RE = /^[A-Za-z0-9_\-:.]{1,128}$/;
const CURRENCY_RE = /^[A-Z]{3}$/;

export function isValidId(v: unknown): v is string {
	return typeof v === "string" && ID_RE.test(v);
}

export function isHoldFlag(v: unknown): v is HoldFlag {
	return (
		typeof v === "string" && (HOLD_FLAGS as readonly string[]).includes(v)
	);
}

export function isHoldStatus(v: unknown): v is HoldStatus {
	return (
		typeof v === "string" &&
		(HOLD_STATUSES as readonly string[]).includes(v)
	);
}

export function isValidCurrency(v: unknown): v is string {
	return typeof v === "string" && CURRENCY_RE.test(v);
}

// ---------------------------------------------------------------------------
// State machine — client mirror of the server rules (used to gate buttons).
// ---------------------------------------------------------------------------

export function canTransition(from: HoldStatus, to: HoldStatus): boolean {
	if (from === to) return false;
	if (from === "approved" || from === "rejected") return false;
	if (from === "pending_review") {
		return to === "approved" || to === "rejected" || to === "escalated";
	}
	if (from === "escalated") {
		return to === "approved" || to === "rejected";
	}
	return false;
}

export function isTerminal(s: HoldStatus): boolean {
	return s === "approved" || s === "rejected";
}

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return new Intl.NumberFormat("en-US").format(Math.trunc(n));
}

export function formatDate(unixSec: number | null): string {
	if (unixSec === null || !Number.isFinite(unixSec) || unixSec <= 0) {
		return "—";
	}
	const d = new Date(unixSec * 1000);
	return d.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

export function formatDateTime(unixSec: number | null): string {
	if (unixSec === null || !Number.isFinite(unixSec) || unixSec <= 0) {
		return "—";
	}
	const d = new Date(unixSec * 1000);
	return d.toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function formatMoneyCents(
	cents: number,
	currency: string | null,
): string {
	if (!Number.isFinite(cents)) return "—";
	const dollars = cents / 100;
	const code = currency ?? "USD";
	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: code,
			minimumFractionDigits: 2,
		}).format(dollars);
	} catch {
		return `${dollars.toFixed(2)} ${code}`;
	}
}

// ---------------------------------------------------------------------------
// Remote bindings
// ---------------------------------------------------------------------------

const BASE = "/igaming-payment-hold";

export interface CreateHoldArgs {
	conversion_id: string;
	affiliate_id: string;
	flag: HoldFlag;
	brand_id?: string | null;
	offer_id?: string | null;
	player_ref?: string | null;
	payout_cents?: number;
	currency?: string | null;
	reason?: string | null;
	flagged_by?: string | null;
}

export async function createHold(args: CreateHoldArgs): Promise<PaymentHold> {
	return apiClient.post<PaymentHold>(BASE, args);
}

export interface ListHoldsArgs {
	status?: HoldStatus;
	flag?: HoldFlag;
	affiliate_id?: string;
	limit?: number;
}

export async function listHolds(
	args: ListHoldsArgs = {},
): Promise<ListResponse> {
	const query: Record<string, string | number> = {};
	if (args.status) query.status = args.status;
	if (args.flag) query.flag = args.flag;
	if (args.affiliate_id) query.affiliate_id = args.affiliate_id;
	if (args.limit !== undefined) query.limit = args.limit;
	return apiClient.get<ListResponse>(BASE, { query });
}

export async function fetchHold(id: string): Promise<PaymentHold> {
	return apiClient.get<PaymentHold>(`${BASE}/${encodeURIComponent(id)}`);
}

export async function fetchHoldEvents(id: string): Promise<EventsResponse> {
	return apiClient.get<EventsResponse>(
		`${BASE}/${encodeURIComponent(id)}/events`,
	);
}

export interface DecisionArgs {
	id: string;
	reviewed_by?: string | null;
	decision_note?: string | null;
}

async function postDecision(
	id: string,
	path: "approve" | "reject" | "escalate",
	body: { reviewed_by?: string | null; decision_note?: string | null },
): Promise<DecisionResponse> {
	const payload: Record<string, unknown> = {};
	if (body.reviewed_by !== undefined) payload.reviewed_by = body.reviewed_by;
	if (body.decision_note !== undefined) {
		payload.decision_note = body.decision_note;
	}
	return apiClient.post<DecisionResponse>(
		`${BASE}/${encodeURIComponent(id)}/${path}`,
		payload,
	);
}

export async function approveHold(args: DecisionArgs): Promise<DecisionResponse> {
	return postDecision(args.id, "approve", {
		reviewed_by: args.reviewed_by,
		decision_note: args.decision_note,
	});
}

export async function rejectHold(args: DecisionArgs): Promise<DecisionResponse> {
	return postDecision(args.id, "reject", {
		reviewed_by: args.reviewed_by,
		decision_note: args.decision_note,
	});
}

export async function escalateHold(args: DecisionArgs): Promise<DecisionResponse> {
	return postDecision(args.id, "escalate", {
		reviewed_by: args.reviewed_by,
		decision_note: args.decision_note,
	});
}

export async function fetchStats(): Promise<StatsSummary> {
	return apiClient.get<StatsSummary>(`${BASE}/stats/summary`);
}

// ---------------------------------------------------------------------------
// Query keys — used by TanStack Query for invalidation after mutations.
// ---------------------------------------------------------------------------

export const igamingPaymentHoldQueryKeys = {
	all: ["igaming-payment-hold"] as const,
	list: (filters: ListHoldsArgs = {}) =>
		[
			...igamingPaymentHoldQueryKeys.all,
			"list",
			filters.status ?? "any",
			filters.flag ?? "any",
			filters.affiliate_id ?? "any",
		] as const,
	byId: (id: string) =>
		[...igamingPaymentHoldQueryKeys.all, "by-id", id] as const,
	events: (id: string) =>
		[...igamingPaymentHoldQueryKeys.all, "events", id] as const,
	stats: () => [...igamingPaymentHoldQueryKeys.all, "stats"] as const,
};
